import React, { useEffect, useState } from "react";
import { Card, Divider, Button } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";

const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return { isMobile, isTablet };
};

const InvoiceSummaryStep = ({ 
  patient,
  booking,
  doctorServices,
  selectedServices,
  subtotal,
  total,
  visitPrice,
  paidAmount
}) => {
  const { isMobile, isTablet } = useResponsive();
  const remainingBalance = (total || 0) - paidAmount;
  const isFullyPaid = remainingBalance <= 0;
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });

  const getSelectedServiceDetails = () => {
    return selectedServices.map(serviceId => {
      const service = doctorServices.find(s => s.id === serviceId);
      return service || null;
    }).filter(Boolean);
  };

  const serviceDetails = getSelectedServiceDetails();

  return (
    <div className="max-w-4xl mx-auto bg-white">
      {/* Header */}
      <div className={`bg-white border-b-4 border-[#298E9F] ${isMobile ? 'p-3' : 'p-6'}`}>
        <div className={`flex ${isMobile ? 'flex-col gap-3' : 'justify-between items-start'}`}>
          <div className="flex items-center gap-3">
            <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-[#298E9F] rounded-full flex items-center justify-center`}>
              <span className={`text-white ${isMobile ? 'text-xl' : 'text-2xl'}`}>🏥</span>
            </div>
            <div>
              <h1 className={`font-bold text-[#298E9F] ${isMobile ? 'text-xl' : 'text-3xl'}`}>Medical Clinic</h1>
              <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>Professional Healthcare Services</p>
            </div>
          </div>
          <div className={isMobile ? 'text-left' : 'text-right'}>
            <h2 className={`font-bold text-[#298E9F] mb-1 ${isMobile ? 'text-2xl' : 'text-4xl'}`}>Invoice</h2>
            <div className={`space-y-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              <p className="text-[#FEBA17] font-semibold">INVOICE #{booking?.id|| '123'}</p>
              <p className="text-gray-600">DATE ISSUED:</p>
              <p className="font-semibold text-[#298E9F]">{today}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={isMobile ? 'p-3' : 'p-6'}>
        {/* Patient Information */}
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-6'} mb-6`}>
          <div>
            <h3 className={`font-bold text-[#FEBA17] uppercase mb-2 tracking-wider ${isMobile ? 'text-xs' : 'text-xs'}`}>Billing To:</h3>
            <div className="space-y-1">
              <p className={`font-bold text-[#298E9F] ${isMobile ? 'text-base' : 'text-lg'}`}>{patient?.fullName || 'NAME OF PATIENT'}</p>
              <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>{patient?.address || 'LOCATION OF PATIENT'}</p>
            </div>
          </div>
          <div className={isMobile ? 'text-left' : 'text-right'}>
            <h3 className={`font-bold text-[#FEBA17] uppercase mb-2 tracking-wider ${isMobile ? 'text-xs' : 'text-xs'}`}>Invoice Details:</h3>
            <div className={`space-y-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              <p className="text-gray-600">Account#: <span className="font-semibold text-[#298E9F]">{patient?.accountId}</span></p>
              <p className="text-gray-600">Booking Date: <span className="font-semibold text-[#298E9F]">{new Date(booking?.bookingDate).toLocaleDateString('en-US') || today}</span></p>
            </div>
          </div>
        </div>

        <Divider className={`${isMobile ? 'my-3' : 'my-4'} border-gray-300`} />

        {/* Services Table */}
        <div className={isMobile ? 'mb-4' : 'mb-6'}>
          {/* Table Header - Hide on mobile */}
          {!isMobile && (
            <div className="bg-[#298E9F] text-white font-bold grid grid-cols-12 gap-4 p-4 text-sm uppercase tracking-wide">
              <div className="col-span-6">Product</div>
              <div className="col-span-2 text-center">Price</div>
              <div className="col-span-2 text-center">QTY</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
          )}

          {/* Visit Price Row */}
          {isMobile ? (
            <div className="p-3 bg-[#e6fdff] border-b-2 border-gray-200 rounded-lg mb-2">
              <p className="font-semibold text-[#298E9F] text-sm">Medical Consultation Visit</p>
              <p className="text-xs text-gray-500 mt-1">Professional medical examination</p>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-600">Qty: 1 × {visitPrice?.toFixed(2)} LE</span>
                <span className="font-bold text-[#298E9F]">{visitPrice?.toFixed(2)} LE</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-4 p-4 bg-[#e6fdff] border-b-2 border-gray-200">
              <div className="col-span-6">
                <p className="font-semibold text-[#298E9F]">Medical Consultation Visit</p>
                <p className="text-xs text-gray-500 mt-1">Professional medical examination and consultation</p>
              </div>
              <div className="col-span-2 text-center text-gray-700 font-semibold">
                {visitPrice?.toFixed(2)} LE
              </div>
              <div className="col-span-2 text-center text-gray-700 font-semibold">1</div>
              <div className="col-span-2 text-right font-bold text-[#298E9F]">
                {visitPrice?.toFixed(2)} LE
              </div>
            </div>
          )}

          {/* Service Rows */}
          {serviceDetails.length > 0 ? (
            serviceDetails.map((service, index) => (
              isMobile ? (
                <div key={service.id} className={`p-3 border-b border-gray-200 rounded-lg mb-2 ${index % 2 === 0 ? 'bg-white' : 'bg-[#e6fdff]'}`}>
                  <p className="font-semibold text-[#298E9F] text-sm">{service.title || service.name}</p>
                  {service.description && (
                    <p className="text-xs text-gray-500 mt-1">{service.description}</p>
                  )}
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">Qty: 1 × {service.price?.toFixed(2)} LE</span>
                    <span className="font-bold text-[#298E9F]">{service.price?.toFixed(2)} LE</span>
                  </div>
                </div>
              ) : (
                <div 
                  key={service.id} 
                  className={`grid grid-cols-12 gap-4 p-4 border-b border-gray-200 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-[#e6fdff]'
                  }`}
                >
                  <div className="col-span-6">
                    <p className="font-semibold text-[#298E9F]">{service.title || service.name}</p>
                    {service.description && (
                      <p className="text-xs text-gray-500 mt-1">{service.description}</p>
                    )}
                  </div>
                  <div className="col-span-2 text-center text-gray-700 font-semibold">
                    {service.price?.toFixed(2)} LE
                  </div>
                  <div className="col-span-2 text-center text-gray-700 font-semibold">1</div>
                  <div className="col-span-2 text-right font-bold text-[#298E9F]">
                    {service.price?.toFixed(2)} LE
                  </div>
                </div>
              )
            ))
          ) : (
            <div className={`${isMobile ? 'p-3' : 'p-4'} bg-gray-50 border-b border-gray-200 text-center text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'} italic`}>
              No additional services selected
            </div>
          )}
        </div>

        {/* Payment Summary */}
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-8'} mt-6`}>
          {/* Payment Info - Hide on mobile */}
          {!isMobile && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xs font-bold text-[#FEBA17] uppercase mb-3 tracking-wider">Payment Info:</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-600">Account#: <span className="font-semibold text-[#298E9F]">1234567890</span></p>
                  <p className="text-gray-600">A/C Name: <span className="font-semibold text-[#298E9F]">{patient?.fullName || 'Patient Name'}</span></p>
                  <p className="text-gray-600">Bank Details: <span className="font-semibold text-[#298E9F]">Add your bank</span></p>
                </div>
              </div>
              <div className="pt-2">
                <p className="text-xs text-gray-500">123 Anywhere St.,</p>
                <p className="text-xs text-gray-500">Any City, ST 12345</p>
                <p className="text-xs text-gray-500">+123-456-7890</p>
                <p className="text-xs text-gray-500">hello@dentalservice.com</p>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className={`space-y-${isMobile ? '2' : '3'} ${isMobile ? 'col-span-1' : ''}`}>
            <Divider className={`${isMobile ? 'my-2' : 'my-2'} border-gray-400`} />
            
            <div className={`flex justify-between items-center bg-[#298E9F] text-white ${isMobile ? 'p-3' : 'p-4'} rounded`}>
              <span className={`font-bold uppercase ${isMobile ? 'text-xs' : ''}`}>Total Price</span>
              <span className={`font-bold ${isMobile ? 'text-xl' : 'text-2xl'}`}>{(total).toFixed(2)} LE</span>
            </div>

            <div className={`flex justify-between items-center bg-green-50 border-2 border-green-400 ${isMobile ? 'p-3' : 'p-4'} rounded`}>
              <span className={`font-bold text-green-800 ${isMobile ? 'text-xs' : ''}`}>Amount Paid</span>
              <span className={`font-bold text-green-600 ${isMobile ? 'text-lg' : 'text-xl'}`}>{paidAmount.toFixed(2)} LE</span>
            </div>

            <div className={`flex justify-between items-center ${isMobile ? 'p-3' : 'p-4'} rounded border-2 ${
              isFullyPaid 
                ? 'bg-green-100 border-green-400' 
                : 'bg-[#FFFBEB] border-[#FDE68A]'
            }`}>
              <span className={`font-bold text-gray-800 ${isMobile ? 'text-xs' : ''}`}>Remaining Balance</span>
              <span className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl'} ${
                isFullyPaid ? 'text-green-600' : 'text-[#FEBA17]'
              }`}>
                {remainingBalance.toFixed(2)} LE
              </span>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        {isFullyPaid && (
          <div className={`${isMobile ? 'mt-4' : 'mt-8'} text-center`}>
            <div className={`inline-flex items-center gap-3 ${isMobile ? 'px-4 py-2' : 'px-8 py-4'} bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-lg`}>
              <CheckCircleOutlined className={isMobile ? 'text-lg' : 'text-2xl'} />
              <span className={`font-bold uppercase tracking-wide ${isMobile ? 'text-sm' : 'text-lg'}`}>Payment Complete</span>
            </div>
          </div>
        )}

        {/* Terms - Hide on mobile */}
        {!isMobile && (
          <div className="mt-8 pt-6 border-t-2 border-gray-300">
            <h4 className="text-xs font-bold text-[#FEBA17] uppercase mb-3 tracking-wider">Terms and Conditions:</h4>
            <p className="text-xs text-gray-600 leading-relaxed">
              Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor 
              incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud 
              exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceSummaryStep;